import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { extractApiError } from '../utils/pdfDownload';
import type { DeliveryTour } from '../types/deliveryTour';
import { ActiveTourView } from '../components/delivery/ActiveTourView';
import { TourHistoryList } from '../components/delivery/TourHistoryList';

const DeliveryBatches: React.FC = () => {
  const [current, setCurrent] = useState<DeliveryTour | null>(null);
  const [history, setHistory] = useState<DeliveryTour[]>([]);
  const [view, setView] = useState<'tour' | 'history' | 'past'>('tour');
  const [past, setPast] = useState<DeliveryTour | null>(null);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const activeId = useRef<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [cur, list] = await Promise.all([
        api.get('/delivery-batches/current'),
        api.get('/delivery-batches', {
          params: showArchived ? { include_archived: 'true' } : undefined,
        }),
      ]);
      setHistory(
        ((list.data || []) as DeliveryTour[]).filter(
          (b) => b.id !== cur.data?.id
        )
      );
      if (cur.data) {
        activeId.current = cur.data.id;
        setCurrent(cur.data);
      } else if (activeId.current) {
        const res = await api.get(`/delivery-batches/${activeId.current}`);
        setCurrent(res.data || null);
      } else {
        setCurrent(null);
      }
    } catch (err) {
      toast.error(extractApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  const abandon = async () => {
    activeId.current = null;
    setCurrent(null);
    setPast(null);
    setView('tour');
    await load();
  };

  useEffect(() => {
    void load();
  }, [load]);

  const openPast = async (id: string) => {
    try {
      const res = await api.get(`/delivery-batches/${id}`);
      setPast(res.data);
      setView('past');
    } catch (err) {
      toast.error(extractApiError(err).message);
    }
  };

  if (loading) {
    return (
      <p className="text-center text-gray-400 font-bold py-20">Loading...</p>
    );
  }

  if (view === 'history') {
    return (
      <div className="max-w-xl mx-auto space-y-4">
        <button
          type="button"
          onClick={() => setView('tour')}
          className="text-sm font-bold text-brand-600"
        >
          ← Back
        </button>
        <h1 className="text-xl font-black">Past tours</h1>
        <label className="flex items-center gap-2 text-sm font-bold text-gray-600">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Include archived
        </label>
        <TourHistoryList
          batches={history}
          onOpen={(id) => void openPast(id)}
          onChanged={() => void load()}
        />
      </div>
    );
  }

  if (view === 'past' && past) {
    return (
      <ActiveTourView
        tour={past}
        onReload={async () => {
          const res = await api.get(`/delivery-batches/${past.id}`);
          setPast(res.data);
          await load();
        }}
        onShowHistory={() => setView('history')}
        onAbandoned={abandon}
      />
    );
  }

  if (current) {
    return (
      <ActiveTourView
        tour={current}
        onReload={load}
        onShowHistory={() => setView('history')}
        onAbandoned={abandon}
      />
    );
  }

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-black text-gray-900">Delivery</h1>
        <p className="text-sm text-gray-500 mt-1">
          Confirm a pick list with a tour to start driving. There is no active
          tour right now.
        </p>
      </div>
      <button
        type="button"
        onClick={() => setView('history')}
        className="w-full min-h-[48px] rounded-xl bg-gray-100 font-bold"
      >
        Past tours
      </button>
    </div>
  );
};

export default DeliveryBatches;
